import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Loader2, ShoppingCart, AlertTriangle, Check, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api';
import { useCart, formatAUD } from '@/context/CartContext';
import Seo from '@/components/Seo';

export default function FastOrder() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState(false);
  const { add, openDrawer } = useCart();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient.get(`/fast-order/${token}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Fast-order link not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const addAll = () => {
    if (!data?.items) return;
    data.items.forEach((it) => {
      add({
        id: it.product_id,
        name: it.name,
        category: it.category,
        unit: it.unit,
        price: it.current_price,
        min_qty: it.min_qty,
        step_qty: it.step_qty,
      }, Number(it.quantity));
    });
    setAdded(true);
    toast.success(`${data.items.length} items added to your cart`);
    setTimeout(() => openDrawer(), 200);
  };

  const subtotal = (data?.items || []).reduce(
    (s, it) => s + it.current_price * (it.quantity || 0),
    0
  );

  return (
    <div className="section-light min-h-screen bg-[var(--mw-bg)] text-white">
      <Seo title="Reorder" path="/reorder" noindex />
      <header className="border-b border-white/10 px-6 py-5">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-[10px] uppercase tracking-[0.32em] text-white/60 hover:text-white">
            ← The Meat Works
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-14">
        {loading ? (
          <div className="flex flex-col items-center py-24 gap-4">
            <Loader2 size={32} className="animate-spin text-white/50" />
            <div className="text-[11px] uppercase tracking-[0.28em] text-white/55">Loading your fast order…</div>
          </div>
        ) : error ? (
          <div data-testid="fast-order-error" className="text-center py-16">
            <div className="mx-auto w-16 h-16 rounded-full bg-[var(--mw-primary)]/15 border border-[var(--mw-primary)]/40 flex items-center justify-center mb-6">
              <AlertTriangle size={28} className="text-[var(--mw-primary)]" />
            </div>
            <h1 className="font-display text-3xl mb-3">{error}</h1>
            <p className="text-white/60">Please reach out to us for a fresh link.</p>
            <Link to="/order" className="inline-block mt-6 text-[11px] uppercase tracking-[0.22em] border border-white/25 hover:border-white text-white px-6 py-3 transition-colors">
              Browse order form
            </Link>
          </div>
        ) : (
          <div data-testid="fast-order-loaded">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-3">Wholesale fast-order</div>
            <h1 className="font-display font-light text-4xl sm:text-5xl leading-tight tracking-tight mb-3">
              Hi{data.customer_name ? `, ${data.customer_name.split(' ')[0]}` : ''} — repeat your order?
            </h1>
            <p className="text-white/60 leading-relaxed mb-8">
              We&apos;ve pulled together your last order. Tap &ldquo;Add all to cart&rdquo; and check out with your usual
              delivery slot — done in under a minute.
            </p>

            <ul className="divide-y divide-white/10 border-y border-white/10">
              {data.items.map((it) => (
                <li key={it.product_id} data-testid={`fast-order-item-${it.product_id}`} className="py-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-display text-lg text-white">{it.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mt-0.5">
                      {it.unit === 'kg' ? `${it.quantity}kg` : `x${it.quantity}`} · {formatAUD(it.current_price)}/{it.unit}
                    </div>
                  </div>
                  <div className="font-display text-lg tabular-nums">{formatAUD(it.current_price * it.quantity)}</div>
                </li>
              ))}
            </ul>

            {data.unavailable?.length > 0 && (
              <div className="mt-4 border border-amber-500/40 bg-amber-500/5 text-amber-300 px-4 py-3 text-[11px] uppercase tracking-[0.18em]">
                {data.unavailable.length} item{data.unavailable.length === 1 ? '' : 's'} from your last order are no longer available and will be skipped.
              </div>
            )}

            <div className="mt-6 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-white/50">Cart subtotal</div>
                <div className="font-display text-3xl tabular-nums">{formatAUD(subtotal)}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mt-1">Prices may vary from your last order</div>
              </div>
              <button
                type="button"
                onClick={addAll}
                data-testid="fast-order-add-all"
                disabled={added}
                className="inline-flex items-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-4 text-xs uppercase tracking-[0.22em] transition-colors"
              >
                {added ? <Check size={14} /> : <ShoppingCart size={14} />}
                {added ? 'Added to cart' : 'Add all to cart'}
              </button>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/order')}
                className="text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-white flex items-center gap-2"
              >
                Continue browsing <ArrowRight size={12} />
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
